import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ContactContext from 'contexts/contactContext'
import ModalContext from 'contexts/ModalContext'
import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import WaitingListRequestFormModal from '../WaitingListRequest/WaitingListRequestFormModal'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import type { DepartureSoldOutReason } from 'lib/tours/tourDepartureUtils'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

const Tile = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  display: grid;
  padding: ${rem(20)};
  gap: ${rem(16)};
  grid-template-areas:
  "dates"
  "call"
  "cta";


  ${mediaQueryUp.tablet} {
    column-gap: ${rem(40)};
    row-gap: ${rem(12)};
    padding: ${rem(16)} 0;
    grid-template-columns: 1fr min-content;
    grid-template-areas:
    "dates cta"
    "call cta";
    align-items: center;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(500)} ${rem(210)} 1fr;
    grid-template-areas: "dates call cta";
  }
`

interface Props {
  offer: App.Tours.TourV2Offer
  travellers: Array<OfferPageRoom>
  reason: DepartureSoldOutReason
  onUpdateTravelers?: () => void
}

function TourV2DeparturesSoldOutTile(props: Props) {
  const {
    offer,
    travellers,
    reason,
    onUpdateTravelers,
  } = props

  const intl = useIntl()
  const contact = useContext(ContactContext)
  const showModal = useContext(ModalContext)

  const onJoinWaitlist = useCallback(() => {
    showModal(<WaitingListRequestFormModal offer={offer} />)
  }, [offer, showModal])

  const callPromptMessage = reason === 'travellers'
    ? (
        <FormattedMessage
          id="YGqUAbvlyy3kfDh19Tcw"
          defaultMessage="Give our team a call on <phoneLink>{phoneNumber}</phoneLink> for more tour options or <updateLink>Update travellers</updateLink>"
          description="Prompt to call or update travellers when no departures match traveller selection"
          values={{
            phoneLink: (chunks) => <TextLink wrap="no-wrap" href={`tel:${contact.defaultContact.number}`}>{chunks}</TextLink>,
            phoneNumber: contact.defaultContact.humanReadable,
            updateLink: (chunks) => <TextLink onClick={onUpdateTravelers}>{chunks}</TextLink>,
          }}
        />
      )
    : (
        <FormattedMessage
          id="1knvjliZzzHTAyK7IGBM"
          defaultMessage="Give our team a call on <phoneLink>{phoneNumber}</phoneLink> for more tour options"
          description="Prompt to call for more tour options when departure is unavailable"
          values={{
            phoneLink: (chunks) => <TextLink wrap="no-wrap" href={`tel:${contact.defaultContact.number}`}>{chunks}</TextLink>,
            phoneNumber: contact.defaultContact.humanReadable,
          }}
        />
      )

  return <Tile>
    <div
      style={{ gridArea: 'dates' }}
    >
      <Heading variant="heading4">{intl.formatMessage({
        id: 'ojnHBFswNuLjeIdJQLm8',
        defaultMessage: 'No departures available',
        description: 'Heading when no tour departures are available in the tours section',
      })}
      </Heading>
      <BodyText variant="medium">
        {reason === 'travellers' && intl.formatMessage(
          {
            id: 'wH2Qy5gmkPeEpol-9zFt',
            defaultMessage: 'There are no departures matching your traveller selection of {travellers}',
            description: 'Message when no tour departures match selected traveller count',
          },
          { travellers: formatOccupantsShort(travellers) },
        )}
        {reason === 'sold-out' && intl.formatMessage({
          id: 'tntzRsOA6IOYeSMN3_JC',
          defaultMessage: 'Sorry, this offer has sold out!',
          description: 'Notification message displayed when tour offer is completely sold out',
        })}
      </BodyText>
    </div>
    <div style={{ gridArea: 'call' }}>
      <BodyText variant="medium">
        {callPromptMessage}
      </BodyText>
    </div>
    <Group
      direction="horizontal"
      gap={8}
      tabletHorizontalAlign="end"
      horizontalAlign="stretch"
      verticalAlign="center"
      style={{ gridArea: 'cta' }}
    >
      <TextButton kind="secondary" onClick={onJoinWaitlist}>{intl.formatMessage({
        id: 'wU1u7MaiQOHg6hdhGJ9Y',
        defaultMessage: 'Join waitlist',
        description: 'Button text to join tour waitlist on the tour offer detail page',
      })}
      </TextButton>
    </Group>
  </Tile>
}

type MappedProps = Pick<Props, 'offer' | 'travellers'>

export default withOfferPageState((state): MappedProps => ({
  offer: state.tourV2Offer!,
  travellers: state.rooms,
}), TourV2DeparturesSoldOutTile)
